import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, PRIORITY_META } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, MessageSquareText, Trash2, ArrowLeft, ListChecks, Upload } from "lucide-react";

const EXAMPLE =
  "Paste your boss's message here. Multiple issues are fine — one per line, e.g.\n\n" +
  "1060- card reader loose, hard to gain entry.\n" +
  "2002- phone cord cut and retired.\n" +
  "2048- alarm hanging, needs full pm soon.";

export default function TextToTaskDialog({ open, onClose, onCreated }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState(null); // null = input step, array = review step
  const fileRef = useRef();

  const reset = () => {
    setText("");
    setDrafts(null);
    setLoading(false);
    setSaving(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => {
    reset();
    onClose();
  };

  const applyDrafts = (items, sourceLabel) => {
    if (!items || items.length === 0) {
      toast.error(`No tasks detected in that ${sourceLabel}`);
      return;
    }
    setDrafts(items.map((d, i) => ({ ...d, _key: `${Date.now()}-${i}` })));
    toast.success(`${items.length} task${items.length > 1 ? "s" : ""} detected — review below`);
  };

  const generate = async () => {
    if (!text.trim()) {
      toast.error("Paste a message first");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/issues/parse", { text });
      applyDrafts(res.data.issues || [], "message");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create tasks from that text";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/issues/parse-file", fd);
      applyDrafts(res.data.issues || [], "file");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not read tasks from that file";
      toast.error(msg);
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateDraft = (key, field, value) =>
    setDrafts((ds) => ds.map((d) => (d._key === key ? { ...d, [field]: value } : d)));

  const removeDraft = (key) => setDrafts((ds) => ds.filter((d) => d._key !== key));

  const createAll = async () => {
    if (!drafts || drafts.length === 0) return;
    setSaving(true);
    try {
      const issues = drafts.map(({ _key, ...d }) => ({
        title: d.title,
        description: d.description || "",
        priority: d.priority,
        property_location: d.property_location || "",
        due_date: d.due_date || null,
      }));
      const res = await api.post("/issues/bulk", { issues, source: "text" });
      toast.success(`${res.data.length} task${res.data.length > 1 ? "s" : ""} created`);
      reset();
      onCreated();
      onClose();
    } catch (e) {
      toast.error("Failed to create tasks");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="text-to-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl flex items-center gap-2">
            {drafts ? <ListChecks className="h-5 w-5" /> : <MessageSquareText className="h-5 w-5" />}
            {drafts ? "Review detected tasks" : "Create tasks from text or a file"}
          </DialogTitle>
          <DialogDescription>
            {drafts
              ? "Each issue became its own task. Edit or remove any, then create them all."
              : "Paste a message or upload a PDF, Excel or CSV. AI splits it into one task per issue and pulls out the unit, priority and due date."}
          </DialogDescription>
        </DialogHeader>

        {!drafts ? (
          <div className="space-y-3">
            <Textarea
              data-testid="text-to-task-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={EXAMPLE}
              className="min-h-[180px]"
            />
            <div className="flex items-center gap-3">
              <div className="h-px bg-zinc-200 flex-1" />
              <span className="text-xs uppercase tracking-wider font-bold text-zinc-400">or</span>
              <div className="h-px bg-zinc-200 flex-1" />
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.xlsx,.xls,.csv,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,text/plain"
              className="hidden"
              onChange={onFile}
              data-testid="file-parse-input"
            />
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={() => fileRef.current?.click()}
              data-testid="upload-parse-file-button"
              className="w-full h-12 gap-2 border-dashed"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload a PDF, Excel or CSV
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="draft-list">
            {drafts.map((d) => {
              const pm = PRIORITY_META[d.priority] || PRIORITY_META.medium;
              return (
                <div key={d._key} className="border border-zinc-200 rounded-md p-3 bg-zinc-50 space-y-2" data-testid="draft-item">
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="draft-title"
                      value={d.title}
                      onChange={(e) => updateDraft(d._key, "title", e.target.value)}
                      className="bg-white font-medium"
                    />
                    <button onClick={() => removeDraft(d._key)} data-testid="draft-remove" className="text-zinc-400 hover:text-red-600 shrink-0 p-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      data-testid="draft-property"
                      value={d.property_location}
                      onChange={(e) => updateDraft(d._key, "property_location", e.target.value)}
                      placeholder="Unit / property"
                      className="bg-white"
                    />
                    <Select value={d.priority} onValueChange={(v) => updateDraft(d._key, "priority", v)}>
                      <SelectTrigger className="bg-white" data-testid="draft-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRIORITY_META).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="date"
                      data-testid="draft-duedate"
                      value={d.due_date || ""}
                      onChange={(e) => updateDraft(d._key, "due_date", e.target.value || null)}
                      className="bg-white"
                    />
                  </div>
                  {d.description && <p className="text-xs text-zinc-500">{d.description}</p>}
                  <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${pm.bg} ${pm.text}`}>
                    {pm.label} priority
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="gap-2">
          {!drafts ? (
            <>
              <Button variant="ghost" onClick={close} data-testid="text-to-task-cancel">Cancel</Button>
              <Button onClick={generate} disabled={loading} data-testid="text-to-task-generate" className="bg-zinc-900 hover:bg-zinc-800 gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Detect tasks
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setDrafts(null)} data-testid="draft-back" className="gap-2">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              <Button onClick={createAll} disabled={saving || drafts.length === 0} data-testid="create-all-button" className="bg-zinc-900 hover:bg-zinc-800 gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Create {drafts.length} task{drafts.length > 1 ? "s" : ""}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
